import type { PlaybackLocation } from '@disco/state-management/selectors/media'
import React from 'react'
import type { TrackTextSettings } from 'types/business'
import type { GroupId } from 'types/group'
import type { Track, TrackId } from 'types/track'
import type { TrackLocation } from 'types/ui'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'
import MediaOverlay from 'components/common/media-overlay'
import TrackArtwork from 'components/common/tracks/track-artwork'
import MediaItem from 'components/ui/media-item'
import getTrackStatus from 'components/utils/track-status'
type Props = {
  hideWatermarkingFlag?: boolean
  track: Track
  textSettings?: TrackTextSettings
  // Playback context
  groupId?: GroupId | null | undefined
  location: TrackLocation
  paneIndex?: number
  sourcePlaylistId?: number | null | undefined
  sourceShortlistId?: string
  tracklist?: TrackId[] | null | undefined
  queue?: boolean
  playbackLocation?: PlaybackLocation | null
}

/**
 * Track information display component with artwork, title, and playback context
 * Handles media overlay integration and track status formatting
 * @param groupId - Group identifier for playback context
 * @param hideWatermarkingFlag - Whether to hide watermarking indicators
 * @param location - Track location context for display logic
 * @param paneIndex - Pane index for playback context
 * @param sourcePlaylistId - Source playlist ID for playback
 * @param sourceShortlistId - Source shortlist ID for playback
 * @param tracklist - Array of track IDs for queue context
 * @param track - Track data to display
 * @param queue - Whether this is a queue context
 * @param textSettings - Text display settings for track info
 */
const TrackInfo = ({
  groupId,
  hideWatermarkingFlag,
  location,
  paneIndex,
  sourcePlaylistId,
  sourceShortlistId,
  tracklist,
  track,
  queue,
  textSettings,
  playbackLocation,
}: Props) => {
  const { line1, line2, isOpenable } = getTrackStatus(track, {
    showWatermarking: location === 'explorer' && !hideWatermarkingFlag,
    textSettings,
  })
  const isAggregated = isAggregatedSearchTrack(track)
  const playbackContext = {
    groupId,
    paneIndex,
    playlistId: sourcePlaylistId,
    shortlistId: sourceShortlistId,
    tracklist: tracklist,
    clientLibraryId: isAggregated
      ? track.origin?.source_library?.id ?? null
      : null,
    queue,
    playbackLocation,
  }
  const media = (
    <MediaOverlay
      disabled={!isOpenable || track.outside_your_territories}
      location={location}
      mediaSource="in-app"
      playbackContext={playbackContext}
      track={track}
    >
      <TrackArtwork track={track} />
    </MediaOverlay>
  )
  return <MediaItem media={media} title={line1} caption={line2} />
}

export default React.memo<Props>(TrackInfo)
